import React, { useEffect, useState, useRef } from 'react';
import api, { fileUrl } from '@/api';
import confetti from 'canvas-confetti';
import { Trophy, X, Buildings, Sparkle } from '@phosphor-icons/react';

const fireConfetti = () => {
  const duration = 3500;
  const animationEnd = Date.now() + duration;
  const colors = ['#6366f1', '#10b981', '#f59e0b', '#ec4899', '#06b6d4'];

  // Burst from left
  confetti({
    particleCount: 80,
    spread: 70,
    origin: { x: 0, y: 0.6 },
    colors,
    startVelocity: 45,
  });
  // Burst from right
  confetti({
    particleCount: 80,
    spread: 70,
    origin: { x: 1, y: 0.6 },
    colors,
    startVelocity: 45,
  });
  // Continuous gentle stream from top
  const interval = setInterval(() => {
    if (Date.now() > animationEnd) { clearInterval(interval); return; }
    confetti({
      particleCount: 30,
      angle: 60,
      spread: 55,
      origin: { x: 0, y: 0.8 },
      colors,
    });
    confetti({
      particleCount: 30,
      angle: 120,
      spread: 55,
      origin: { x: 1, y: 0.8 },
      colors,
    });
  }, 250);
};

const CelebrationOverlay = () => {
  const [pending, setPending] = useState([]);
  const [activeIdx, setActiveIdx] = useState(0);
  const [loaded, setLoaded] = useState(false);
  const fired = useRef(false);

  useEffect(() => {
    const fetchPending = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;
        const res = await api.get('/startup/celebrations/pending');
        setPending(res.data || []);
      } catch { /* swallow */ }
      finally { setLoaded(true); }
    };
    fetchPending();
  }, []);

  useEffect(() => {
    if (loaded && pending.length > 0 && !fired.current) {
      fired.current = true;
      // Tiny delay so the modal mounts before confetti fires
      setTimeout(fireConfetti, 200);
    }
  }, [loaded, pending]);

  const dismissCurrent = async () => {
    const current = pending[activeIdx];
    if (!current) return;
    try {
      await api.post(`/startup/celebrations/${current.id}/seen`, {});
    } catch { /* swallow */ }
    
    if (activeIdx + 1 < pending.length) {
      setActiveIdx(activeIdx + 1);
      setTimeout(fireConfetti, 200);
    } else {
      setPending([]);
    }
  };

  if (!loaded || pending.length === 0) return null;
  const current = pending[activeIdx];
  const isIncubator = current.entity_type === 'incubator';

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[100] p-4 animate-in fade-in duration-300" data-testid="celebration-overlay">
      <div className={`relative bg-surface border-2 ${isIncubator ? 'border-warning' : 'border-primary'} rounded-2xl p-8 max-w-md w-full shadow-2xl`}>
        {pending.length > 1 && (
          <div className="absolute top-3 right-3 text-[11px] font-mono text-text-muted bg-background border border-border rounded-full px-2 py-0.5">
            {activeIdx + 1} / {pending.length}
          </div>
        )}

        <div className="text-center">
          <div className={`inline-flex items-center justify-center w-20 h-20 rounded-full ${isIncubator ? 'bg-warning/15' : 'bg-primary/15'} mb-5 relative`}>
            <Trophy size={40} weight="fill" className={isIncubator ? 'text-warning' : 'text-primary'} />
            <Sparkle size={16} weight="fill" className="absolute -top-1 -right-1 text-warning animate-pulse" />
            <Sparkle size={12} weight="fill" className="absolute -bottom-1 -left-1 text-primary animate-pulse" />
          </div>
          <div className="text-[11px] uppercase tracking-wider text-text-muted mb-2 font-mono">You got in!</div>
          <h2 className="text-3xl font-heading font-bold text-text-primary mb-3">
            Welcome to <span className={isIncubator ? 'text-warning' : 'text-primary'}>{current.program_name}</span>
          </h2>
          <div className="flex items-center justify-center gap-2 mb-5">
            {current.fund_logo_url ? (
              <img src={fileUrl(current.fund_logo_url)} className="w-7 h-7 rounded object-cover" alt="" />
            ) : (
              <div className={`w-7 h-7 rounded ${isIncubator ? 'bg-warning/15' : 'bg-primary/15'} flex items-center justify-center`}>
                <Buildings size={14} className={isIncubator ? 'text-warning' : 'text-primary'} weight="bold" />
              </div>
            )}
            <span className="text-sm text-text-secondary">by <strong className="text-text-primary">{current.fund_name}</strong></span>
          </div>
          <p className="text-sm text-text-secondary mb-6 leading-relaxed">
            Congratulations! You've been accepted. {isIncubator ? 'The program team' : 'The investor'} will reach out with next steps soon.
          </p>
          <button
            onClick={dismissCurrent}
            className={`w-full py-3 rounded-xl text-white font-medium hover:opacity-90 transition-opacity ${isIncubator ? 'bg-warning' : 'bg-primary'}`}
            data-testid="celebrate-dismiss-btn"
          >
            🎉 {pending.length - activeIdx > 1 ? 'See next acceptance' : "Let's go!"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CelebrationOverlay;
